import { Box } from "@mui/material";
import Head from "next/head";
import Link from "next/link";
import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_EASE,
  SITE_HEADER_HEIGHT_PX,
  SITE_HEADER_OUTER_HEIGHT_PX,
  SITE_HEADER_TOP_VAR,
  SITE_MEDIA,
  siteFluidFont,
} from "src/domains/shared/constants/styles/site";
import { siteDirPreview, siteDirUrl } from "../siteKit/media/siteMedia";
import { useReducedMotion } from "../siteKit/motion/reducedMotion";
import { replayOnEnterRef } from "../siteKit/motion/reveal";
import { SITE_BUTTON_STYLES } from "../siteKit/siteButtonStyles";
import { enterOrder, SITE_HERO_ENTER_STYLES } from "../siteKit/siteHeroStyles";
import { DroneSquareMark } from "../SiteShell/DroneSquareMark";
import { SiteTitle } from "../siteKit/SiteText";
import { useStartHref } from "../siteKit/useStartHref";
import {
  createStoryEngine,
  STAGE_READY_ATTR,
  STORY_HEIGHT_FACTOR,
} from "./storyEngine";
import {
  DRONE_ASPECT,
  DRONE_ROTOR_CELL,
  DRONE_ROTOR_SPIN_OPACITY,
  DRONE_ROTOR_SPIN_PERIOD,
  DRONE_WIDTH_PHONE_PX,
  DRONE_WIDTH_PX,
} from "./storyDrone";
import {
  STORY_FRAME_COUNT,
  STORY_POSTER_IMAGE,
  STORY_TOP_IMAGE,
} from "./storyMedia";

/** 첫 장면 표시 — 구역 눈금(`SectionRail`)이 사이트 구역과 함께 센다 */
export const STORY_HERO_ATTR = "data-story-hero";

const frameName = (index: number) =>
  `fr/r${String(index).padStart(2, "0")}.webp`;

/** 첫 화면 푸른 그라데이션(시안 G6) — 스크롤 진행 60% 까지 걷힌다 */
const HERO_GRADIENT = [
  "radial-gradient(36% 28% at 14% 80%,rgba(160,210,255,.45) 0%,rgba(160,210,255,0) 70%)",
  "radial-gradient(30% 24% at 44% 66%,rgba(60,120,235,.3) 0%,rgba(60,120,235,0) 70%)",
  "radial-gradient(38% 30% at 72% 76%,rgba(160,210,255,.4) 0%,rgba(160,210,255,0) 70%)",
  "radial-gradient(30% 24% at 94% 60%,rgba(60,120,235,.26) 0%,rgba(60,120,235,0) 70%)",
  "radial-gradient(40% 30% at 30% 97%,rgba(60,120,235,.5) 0%,rgba(60,120,235,0) 70%)",
  "radial-gradient(42% 30% at 78% 98%,rgba(150,205,255,.55) 0%,rgba(150,205,255,0) 70%)",
  "linear-gradient(180deg,rgba(60,120,235,0) 70%,rgba(60,120,235,.252) 88%,rgba(60,120,235,.7) 100%)",
].join(",");

const GNB = `${SITE_HEADER_HEIGHT_PX}px`;
/** 머리말이 문서에서 차지하는 높이(테두리 포함) — 첫 장면은 그 뒤까지 올라가 화면 전체를 무대로 쓴다 */
const GNB_OUTER = `${SITE_HEADER_OUTER_HEIGHT_PX}px`;
/** 이야기 구간 기본 높이 — 엔진이 재는 값과 같아 엔진이 늦게 붙어도 아래 구역이 밀리지 않는다 */
const STORY_DEFAULT = `calc(${STORY_HEIGHT_FACTOR} * 100vh)`;
const STORY_DEFAULT_SVH = `calc(${STORY_HEIGHT_FACTOR} * 100svh)`;
/** 장면 데이터가 오기 전 깔리는 흐린 미리보기 그림(`url(...)`) — 그림 틀이 `::before` 배경으로 쓴다 */
const STORY_PREVIEW_VAR = "--preview";

/**
 * 무대(pin)·카드·장면은 모두 화면 크기로 고정하고, 머리말 자리·둥근 카드의 펼침은 카드의 잘라내기(clip-path) 하나로만 그린다 —
 * 스크롤 프레임마다 또는 머리말이 숨을 때 레이아웃이 바뀌지 않아 끊김·레이아웃 이동이 없다. 머리말 자리(`SITE_HEADER_TOP_VAR`)는
 * 머리말이 미끄러지는 것과 같은 박자로 바뀌어 카드 위 가장자리가 머리말 아래 끝을 따라간다.
 */
const STORY_STYLES = {
  root: {
    "--inset": "clamp(8px,1.1vw,16px)",
    "--rad": "clamp(24px,3.1vw,44px)",
    "--story": STORY_DEFAULT,
    position: "relative",
    // 머리말 뒤까지 올라간다 — 머리말이 보이는 동안은 무대 위쪽이 머리말 아래에서 잘린다
    marginTop: `calc(-1 * ${GNB_OUTER})`,
    height: "calc(100vh + var(--story))",
    "@supports (height: 100svh)": {
      "--story": STORY_DEFAULT_SVH,
      height: "calc(100svh + var(--story))",
    },
    padding: 0,
    scrollMarginTop: GNB,
  },
  pin: {
    position: "sticky",
    top: 0,
    height: "100vh",
    "@supports (height: 100svh)": { height: "100svh" },
    // 아이폰에서 엔진이 붙기 전까지 잘라낸 카드 밖이 검게 보였다(sticky 무대의 레이어에 칠해지지 않은 자리로 추정) — 무대가 바탕을 직접 칠한다
    background: SITE_COLOR.bg,
    "--k": 0,
    "--i": "calc(var(--inset) * (1 - var(--k)))",
  },
  card: {
    position: "absolute",
    inset: 0,
    // 둥근 카드가 화면으로 펼쳐지는 모양(안쪽 여백·모서리)과 머리말 아래 자리는 잘라내기로만 — 안의 무대·그림 크기는 그대로다
    clipPath: `inset(calc(var(${SITE_HEADER_TOP_VAR},${GNB_OUTER}) + var(--i)) var(--i) var(--i) var(--i) round calc(var(--rad) * (1 - var(--k))))`,
    background: "#EEF3FA",
    isolation: "isolate",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: -1,
      background: HERO_GRADIENT,
      pointerEvents: "none",
      opacity: "calc(1 - min(1, var(--p,0) / .6))",
    },
  },
  // 장면은 화면 전체 크기(머리말이 숨은 크기) 그대로 — 머리말이 보이는 동안은 위쪽이 잘려 있다가 숨으면 드러난다
  scene: {
    position: "absolute",
    inset: 0,
    margin: 0,
    zIndex: -2,
    background: "#DDEBF8",
    overflow: "hidden",
    contain: "layout paint",
    // 장면 데이터가 오기 전 미리보기(흐린 미리보기 → 정사영상 포스터) — 둘 다 엔진의 첫 그림(옅은 회색 지도)과 같은 톤으로.
    // 흐린 미리보기를 색 그대로 두면 포스터가 오기 전 화면이 온전한 색 사진처럼 덮였다가 회색으로 돌아가 깜빡인다
    "&::before, & > img": {
      position: "absolute",
      inset: 0,
      filter: "grayscale(.85)",
      opacity: 0.9,
    },
    "&::before": {
      content: '""',
      backgroundImage: `var(${STORY_PREVIEW_VAR},none)`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    },
    "& > img": {
      width: "100%",
      height: "100%",
      display: "block",
      objectFit: "cover",
      maxWidth: "none",
    },
  },
  stage: {
    position: "absolute",
    inset: 0,
    // 엔진의 첫 그림이 그려지면 미리보기 위로 부드럽게 나타난다 — 미리보기와 톤이 달라 한 번에 바뀌면 깜빡여 보인다
    opacity: 0,
    transition: "opacity .4s ease",
    [`&[${STAGE_READY_ATTR}]`]: { opacity: 1 },
    "& > *": { position: "absolute" },
    "& canvas": { inset: 0, width: "100%", height: "100%", display: "block" },
    "& .foot": { inset: 0, pointerEvents: "none" },
    "& .fp": {
      position: "absolute",
      opacity: 0,
      transform: "translate(-50%,-50%) rotate(var(--r)) scale(.92)",
      backgroundRepeat: "no-repeat",
      filter: "drop-shadow(0 10px 14px rgba(20,30,50,.34))",
      outline: "2px solid rgba(255,255,255,.9)",
      outlineOffset: "-2px",
      transition: `opacity .35s ease,transform .5s ${SITE_EASE.out},filter .6s ease,outline-color .6s ease`,
    },
    "& .fp.on": {
      opacity: 1,
      transform: "translate(-50%,-50%) rotate(var(--r)) scale(1)",
    },
    "& .fp.set": {
      transform: "translate(-50%,-50%) rotate(0) scale(1)",
      filter: "drop-shadow(0 0 0 rgba(20,30,50,0))",
      outlineColor: "rgba(255,255,255,0)",
    },
    "& .fp.off": { opacity: 0, transition: "opacity .7s ease" },
    "& svg.path": {
      inset: 0,
      width: "100%",
      height: "100%",
      overflow: "visible",
      transition: "opacity .7s ease",
    },
    "& svg.path path": {
      fill: "none",
      stroke: "#fff",
      strokeWidth: 2,
      strokeDasharray: "6 7",
      strokeLinecap: "round",
      opacity: 0.95,
      filter: "drop-shadow(0 1px 2px rgba(0,0,0,.3))",
    },
    "& .live": {
      width: 0,
      height: 0,
      border: "1.5px solid rgba(255,255,255,.95)",
      borderRadius: "3px",
      transform: "translate(-50%,-50%)",
      boxShadow: "0 0 0 1px rgba(40,70,110,.25)",
      opacity: 0,
    },
    // 그림자는 바깥 틀에 둔다 — 안쪽 `.heading` 만 진행 방향으로 돌아 그림자 방향은 화면 기준으로 남는다
    "& .drone": {
      width: `${DRONE_WIDTH_PX}px`,
      height: `${DRONE_WIDTH_PX / DRONE_ASPECT}px`,
      transform: "translate(-50%,-50%)",
      opacity: 0,
      filter: "drop-shadow(0 14px 12px rgba(20,40,70,.35))",
      [SITE_MEDIA.phone]: {
        width: `${DRONE_WIDTH_PHONE_PX}px`,
        height: `${DRONE_WIDTH_PHONE_PX / DRONE_ASPECT}px`,
      },
    },
    "& .drone .heading": { position: "absolute", inset: 0 },
    "& .drone img": {
      width: "100%",
      height: "100%",
      display: "block",
      maxWidth: "none",
    },
    // 프로펠러 칸: 가운데가 모터 축이라 제자리에서 돈다. 위치·크기는 몸체 그림 비율(margin 의 % 는 가로 기준), 그림은 마크업이 넣는다
    "& .drone .rt": {
      position: "absolute",
      width: `${DRONE_ROTOR_CELL.width * 100}%`,
      height: `${DRONE_ROTOR_CELL.height * 100}%`,
      marginLeft: `${-DRONE_ROTOR_CELL.width * 50}%`,
      marginTop: `${-DRONE_ROTOR_CELL.width * 50}%`,
      backgroundSize: "200% 200%",
      backgroundRepeat: "no-repeat",
      animation: `storyRotor ${DRONE_ROTOR_SPIN_PERIOD} linear infinite`,
      opacity: DRONE_ROTOR_SPIN_OPACITY,
    },
    // 도는 날개 아래 옅은 원반 — 빠르게 도는 프로펠러가 사진에서 반투명 원으로 보이는 것을 흉내 낸다
    "& .drone .rt::before": {
      content: '""',
      position: "absolute",
      inset: "6%",
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(30,34,40,.2) 0 68%, rgba(30,34,40,0) 100%)",
    },
    "@keyframes storyRotor": { to: { transform: "rotate(360deg)" } },
    [SITE_MEDIA.reducedMotion]: {
      transition: "none",
      "& .drone .rt": { animation: "none", opacity: 1 },
      "& .drone .rt::before": { display: "none" },
    },
  },
  fade: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "46%",
    zIndex: 1,
    background:
      "linear-gradient(180deg,rgba(255,255,255,0) 0%,rgba(255,255,255,.5) 42%,rgba(255,255,255,.9) 74%,#fff 100%)",
    opacity: "var(--f,0)",
    pointerEvents: "none",
  },
  // 글은 머리말 아래 공간의 가운데 — 머리말이 숨어도 움직이지 않는다
  copy: {
    ...SITE_HERO_ENTER_STYLES,
    position: "absolute",
    left: "50%",
    top: `calc(50% + ${SITE_HEADER_HEIGHT_PX / 2}px)`,
    transform: "translate(-50%,-52%)",
    width: "min(1100px,calc(100% - 160px))",
    textAlign: "center",
    color: "#fff",
    zIndex: 3,
    opacity: "calc(1 - var(--f,0))",
    [SITE_MEDIA.tablet]: { width: "calc(100% - 40px)" },
    // 세로 휴대폰: 로고·브랜드명·부제목과 버튼을 한 열로 묶어 무대 가운데보다 조금 위(아래 6% 를 비운 칸의 가운데)에 둔다 —
    // 버튼이 화면 아래 모서리가 아니라 엄지가 정확히 닿는 가운데 아래쪽에 온다(marketing-landing). 자식의 등장 애니메이션이
    // transform 을 쓰므로 transform 으로 맞추지 않는다
    [SITE_MEDIA.phone]: {
      left: "20px",
      right: "20px",
      top: GNB,
      bottom: "calc(6% + env(safe-area-inset-bottom,0px))",
      width: "auto",
      transform: "none",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      pointerEvents: "none",
      "& > *": { pointerEvents: "auto" },
    },
  },
  title: {
    color: "#fff",
    fontWeight: 700,
    letterSpacing: "-.02em",
    lineHeight: 1.1,
    fontSize: siteFluidFont(18, 2.1, 30),
    textWrap: "balance",
    textShadow:
      "0 0 7px rgba(6,10,16,.36),0 0 16px rgba(6,10,16,.18),0 2px 5px rgba(6,10,16,.3)",
    "& > span": { display: "block" },
    "& > [data-logo]": { display: "none" },
    "& [data-brand]": {
      color: "#fff",
      fontWeight: 800,
      fontSize: siteFluidFont(64, 11.7, 168),
      letterSpacing: "-.045em",
      lineHeight: 1.02,
      marginTop: ".04em",
      textShadow:
        "0 0 7px rgba(29,116,255,.36),0 0 18px rgba(29,116,255,.2),0 2px 5px rgba(6,10,16,.28)",
    },
    [SITE_MEDIA.tablet]: {
      fontSize: siteFluidFont(24, 6.4, 40),
      "& [data-brand]": { fontSize: siteFluidFont(40, 11.6, 72) },
    },
    // 세로 휴대폰(앱 아이콘형): 큰 앱 아이콘 타일 로고 → 브랜드명 → 한 줄 부제목 「드론 기반 인프라 관리 플랫폼」
    // (보이는 순서만 바꾸고 읽는 순서는 그대로). 로고가 주인공이라 브랜드명은 화면 폭의 1/8 로 줄인다
    [SITE_MEDIA.phone]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      fontSize: siteFluidFont(16, 4.6, 18),
      fontWeight: 600,
      lineHeight: 1.45,
      "& > span:not([data-brand]):not([data-logo])": { marginTop: "6px" },
      "& > [data-logo]": {
        order: -2,
        display: "grid",
        placeItems: "center",
        width: "92px",
        height: "92px",
        marginBottom: "20px",
        borderRadius: "24px",
        background: "linear-gradient(150deg,#FFFFFF 0%,#EEF3FF 100%)",
        boxShadow:
          "0 14px 30px -10px rgba(8,24,60,.5),inset 0 0 0 1px rgba(255,255,255,.7)",
        "& svg": { width: "60%", height: "60%", display: "block" },
      },
      "& [data-brand]": {
        order: -1,
        fontSize: "calc((100vw - 40px) / 8)",
        lineHeight: 1.04,
        marginTop: 0,
      },
    },
  },
  actions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: "48px",
    "& a": { height: "50px", padding: "0 24px", fontSize: "16px" },
    // 세로 휴대폰: 버튼을 한 줄씩 전체 폭으로 쌓는다(문의하기가 위)
    [SITE_MEDIA.phone]: {
      flexDirection: "column",
      marginTop: "40px",
      "& a": { width: "100%", height: "56px", fontSize: "17px" },
    },
  },
  // 시작하기: 투명 대신 흰 바탕
  start: {
    background: "rgba(255,255,255,.86)",
    borderColor: "rgba(255,255,255,.86)",
    color: "#1C1F25",
    fontWeight: 600,
    boxShadow: "0 4px 14px rgba(16,40,88,.14)",
    "&:hover": {
      background: "rgba(255,255,255,.28)",
      borderColor: "#F2F4F6",
      color: "#1C1F25",
    },
  },
} as const;

/** 홈 첫 장면(marketing-landing): 둥근 카드가 화면으로 펼쳐지며 드론 촬영 → 정사영상 → 3D 이야기가 스크롤을 따라간다. */
export function StoryHero() {
  const { t } = useTranslation("landing");
  const titleId = useId();
  const startHref = useStartHref();
  const reducedMotion = useReducedMotion();
  const rootRef = useRef<HTMLElement | null>(null);
  const pinRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const topUrl = siteDirUrl("story", STORY_TOP_IMAGE);

  // 엔진은 첫 화면 코드와 함께 받는다(따로 받으면 하이드레이션 뒤 한 번 더 기다린다)
  useEffect(() => {
    const root = rootRef.current;
    const pin = pinRef.current;
    const stage = stageRef.current;
    if (!root || !pin || !stage) return undefined;
    const engine = createStoryEngine({
      root,
      pin,
      stage,
      topUrl,
      frameUrl: (index) => siteDirUrl("story", frameName(index)),
      frameCount: STORY_FRAME_COUNT,
      reducedMotion,
    });
    return () => engine.dispose();
  }, [reducedMotion, topUrl]);

  const preview = siteDirPreview("story");
  return (
    <Box
      component="section"
      ref={rootRef}
      {...{ [STORY_HERO_ATTR]: "" }}
      aria-labelledby={titleId}
      sx={STORY_STYLES.root}
    >
      {/* 엔진의 첫 그림(정사영상)은 스크립트를 받는 동안 미리 받아 둔다 — 엔진이 fetch 로 받으므로 같은 종류(fetch·cors)로.
          먼저 보이는 것은 포스터라 정사영상은 낮은 우선순위로 — 포스터와 회선을 나누면 포스터가 늦어 미리보기가 그만큼 오래 보인다 */}
      <Head>
        <link
          key="preload-story-top"
          rel="preload"
          as="fetch"
          href={topUrl}
          crossOrigin="anonymous"
          fetchPriority="low"
        />
      </Head>
      <Box ref={pinRef} sx={STORY_STYLES.pin}>
        <Box sx={STORY_STYLES.card}>
          <Box
            component="figure"
            role="img"
            aria-label={t("site.landing.hero.sceneLabel")}
            sx={[
              STORY_STYLES.scene,
              preview ? { [STORY_PREVIEW_VAR]: `url(${preview})` } : {},
            ]}
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- 사이트 미디어는 파이프라인이 만든 해시 파일을 그대로 쓴다(next/image 최적화 대상 아님) */}
            <img
              src={siteDirUrl("story", STORY_POSTER_IMAGE)}
              alt=""
              decoding="async"
              fetchPriority="high"
            />
            <Box ref={stageRef} sx={STORY_STYLES.stage} />
          </Box>
          <Box aria-hidden="true" sx={STORY_STYLES.fade} />
        </Box>
        <Box ref={replayOnEnterRef} sx={STORY_STYLES.copy}>
          <Box component="h1" id={titleId} sx={STORY_STYLES.title}>
            <span data-logo="">
              <DroneSquareMark />
            </span>
            <span>
              <SiteTitle text={t("site.landing.hero.title")} />
            </span>
            <span data-brand="">DroneSquare</span>
          </Box>
          <Box style={enterOrder(1)} sx={STORY_STYLES.actions}>
            <Box
              component="a"
              href="#contact"
              sx={SITE_BUTTON_STYLES.primaryLarge}
            >
              {t("site.landing.hero.ask")}
            </Box>
            <Box
              component={Link}
              href={startHref}
              sx={[SITE_BUTTON_STYLES.outlineLarge, STORY_STYLES.start]}
            >
              {t("site.landing.hero.start")}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
